/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import Spacer from 'components/Spacer';
import Toaster from 'components/Toaster';
import Images from 'images';
import { generateSekretKey } from 'shared/generateSecretKey';
import storage from 'shared/storage';
import { makeClient } from '../../apollo';
import AuthPhoneInput, {
  formatPhoneForDisplay,
} from './components/AuthPhoneInput';
import OtpInput from './components/OtpInput';
import Login, { REQUEST_CODE, REQUEST_EMAIL_CODE } from './gql/login';
import verifyCodeV2 from './gql/verify-code';
import HelpLink from './PromoterSignup/components/HavingTrouble';

const platform = process.env.REACT_APP_PLATFORM;

const getLogo = () => {
  if (platform === 'tickethoss') return Images.ticketHossDarkBgLogo;
  if (platform === 'pitpay') return Images.pitpayLoginLogo;
  if (platform === 'kartpass') return Images.logoKart;
  return Images.logoHorizontalWhite;
};

const LOGIN_ROUTE_PHONE = '/login/phone';
const LOGIN_ROUTE_VERIFY = '/login/verify';
const LOGIN_ROUTE_EMAIL = '/login/email';
const LOGIN_PENDING_STORAGE_KEY = 'pending-login';
const LOGIN_PENDING_TTL_MS = 15 * 60 * 1000;

const isPhonePath = (pathname) =>
  pathname === '/' || pathname === '/login' || pathname === LOGIN_ROUTE_PHONE;
const isEmailPath = (pathname) => pathname === LOGIN_ROUTE_EMAIL;

const extractRequestErrorMessage = (error, fallbackMessage) => {
  if (!error) return fallbackMessage;
  if (typeof error === 'string') return error;

  const graphQLErrorMessage = error?.graphQLErrors?.[0]?.message;
  if (graphQLErrorMessage) return graphQLErrorMessage;

  const networkErrorMessage = error?.networkError?.result?.errors?.[0]?.message;
  if (networkErrorMessage) return networkErrorMessage;

  const nestedErrorMessage =
    error?.errors?.message || Object.values(error?.errors || {})?.[0];
  if (typeof nestedErrorMessage === 'string' && nestedErrorMessage.length) {
    return nestedErrorMessage;
  }

  if (typeof error?.message === 'string' && error.message.length) {
    return error.message;
  }

  return fallbackMessage;
};

const PageContainer = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 32px;
  box-sizing: border-box;
  overflow-x: hidden;
  font-family: ${(props) =>
    props.theme?.fonts?.primary ||
    '-apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif'};

  @media (max-width: 768px) {
    justify-content: center;
    padding: 20px 12px 24px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  @media (max-width: 760px) {
    background: linear-gradient(
      135deg,
      #080812 0%,
      #0d1020 35%,
      #111a32 65%,
      #0a1228 100%
    );
  }

  @media (max-width: 768px) {
    &:focus-within {
      justify-content: flex-start;
      padding-top: max(12px, env(safe-area-inset-top));
      padding-bottom: calc(24px + env(safe-area-inset-bottom));
    }
  }

  @media (max-width: 768px) and (max-height: 740px) {
    justify-content: flex-start;
  }
`;

const ContentCard = styled.div`
  width: min(100%, 436px);
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 48px 44px 56px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 32px 20px 36px;
  }

  @media (max-width: 760px) {
    background: rgba(12, 16, 32, 0.92);
    border-color: rgba(255, 255, 255, 0.16);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5),
      inset 0 1px 0 rgba(255, 255, 255, 0.05);
  }
`;

const Logo = styled.img`
  display: block;
  margin: 0 auto 28px;
  width: 180px;
  height: auto;

  @media (max-width: 768px) {
    width: 150px;
  }
`;

const Title = styled.h1`
  color: #ffffff;
  font-size: 26px;
  text-align: center;
  font-weight: 700;
  margin: 0 0 16px 0;

  @media (max-width: 768px) {
    font-size: 27px;
  }
`;

const Subtitle = styled.p`
  color: #94a3b8;
  font-size: 15px;
  text-align: center;
  margin: 0 0 28px 0;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 16px;
  }

  @media (max-width: 760px) {
    color: #b4c4d6;
  }
`;

const MaskedEmailText = styled.p`
  color: #94a3b8;
  font-size: 15px;
  text-align: center;
  font-weight: 500;
  margin: 0 0 16px 0;

  @media (max-width: 768px) {
    font-size: 16px;
  }

  @media (max-width: 760px) {
    color: #b4c4d6;
  }
`;

const PhoneDisplay = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 8px;
  margin-bottom: 28px;
`;

const PhoneNumber = styled.span`
  color: #ffffff;
  font-size: 20px;
  font-weight: 600;
  line-height: 1;

  @media (max-width: 768px) {
    font-size: 21px;
  }
`;

const EditButton = styled.button`
  color: #fa4616;
  font-size: 16px;
  cursor: pointer;
  font-weight: 600;
  line-height: 1;
  background: transparent;
  border: none;
  padding: 0;
  font-family: inherit;

  @media (max-width: 768px) {
    font-size: 17px;
  }

  &:hover {
    text-decoration: underline;
  }
`;

const ErrorText = styled.p`
  color: #ef4444;
  font-size: 14px;
  text-align: center;
  margin: 16px 0;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const ResendLink = styled.button`
  display: block;
  text-align: center;
  color: #fa4616;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  margin-top: 20px;
  background: transparent;
  border: none;
  padding: 0;
  width: 100%;
  font-family: inherit;

  @media (max-width: 768px) {
    font-size: 16px;
  }

  &:hover {
    text-decoration: underline;
  }
`;

const EmailFallbackLink = styled.button`
  display: block;
  text-align: center;
  color: #fa4616;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 20px;
  background: transparent;
  border: none;
  border-top: 1px solid rgba(148, 163, 184, 0.2);
  padding: 12px 0 0;
  width: 100%;
  font-family: inherit;

  @media (max-width: 768px) {
    font-size: 16px;
  }

  @media (max-width: 760px) {
    border-top-color: rgba(148, 163, 184, 0.3);
  }

  &:hover {
    text-decoration: underline;
    color: #ff6a3a;
  }
`;

const ContinueButton = styled.button`
  display: block;
  width: 100%;
  padding: 16px;
  margin-top: 20px;
  background: #fa4616;
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  font-family: inherit;
  border: none;
  border-radius: 8px;
  box-sizing: border-box;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #e03e12;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    font-size: 17px;
  }
`;

const HelpHint = styled.div`
  display: grid;
  grid-template-columns: 20px 1fr;
  column-gap: 12px;
  align-items: center;
  text-align: left;
  margin-top: 18px;
  padding: 14px 16px;
  border-radius: 8px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  background: rgba(30, 41, 59, 0.78);

  @media (max-width: 760px) {
    background: rgba(18, 26, 45, 0.9);
    border-color: rgba(148, 163, 184, 0.3);
  }
`;

const HelpHintIcon = styled.span`
  color: #d7e1f3;
  width: 20px;
  height: 20px;
  margin-top: 0;

  svg {
    width: 100%;
    height: 100%;
  }
`;

const HelpHintContent = styled.div`
  flex: 1;
`;

const HelpHintTitle = styled.p`
  margin: 0;
  color: #f8fafc;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const HelpHintBody = styled.p`
  margin: 4px 0 0;
  color: #a6b4cb;
  font-size: 13px;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 14px;
  }

  @media (max-width: 760px) {
    color: #bcc8d8;
  }
`;

const EmailSuccessNotice = styled.div`
  margin: 0 0 14px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid rgba(74, 222, 128, 0.35);
  background: rgba(34, 197, 94, 0.14);
  color: #d1fae5;
  font-size: 13px;
  font-weight: 600;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const Auth = (props) => {
  const history = useHistory();
  const location = useLocation();
  const pathname = location.pathname;
  const defaultRedirect = '/admin/users';
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState([
    '',
    '',
    '',
    '',
    '',
    '',
  ]);
  const [countryCode, setCountryCode] = useState('1');
  const [error, setError] = useState('');
  const [verificationTried, setVerificationTried] = useState(false);
  const [codeSentVia, setCodeSentVia] = useState('sms');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailNotice, setEmailNotice] = useState('');
  const [toaster, setToaster] = useState({
    isOpen: false,
    type: 'error',
    title: '',
    message: '',
  });

  const showErrorToaster = (title, error, fallbackMessage) => {
    setToaster({
      isOpen: true,
      type: 'error',
      title,
      message: extractRequestErrorMessage(error, fallbackMessage),
    });
  };

  const getPendingLogin = () => {
    const pending = storage.get(LOGIN_PENDING_STORAGE_KEY, null);
    if (!pending || typeof pending !== 'object') return null;

    const timestamp = Number(pending.updated_at);
    if (!timestamp || Date.now() - timestamp > LOGIN_PENDING_TTL_MS) {
      storage.removeItem(LOGIN_PENDING_STORAGE_KEY);
      return null;
    }

    if (!pending.phone || !pending.calling_code) {
      storage.removeItem(LOGIN_PENDING_STORAGE_KEY);
      return null;
    }

    return pending;
  };

  const savePendingLogin = ({ step, sentVia, nextMaskedEmail = '' }) => {
    if (!phoneNumber || !countryCode) return;
    storage.set(LOGIN_PENDING_STORAGE_KEY, {
      phone: phoneNumber,
      calling_code: countryCode,
      step,
      sent_via: sentVia,
      masked_email: nextMaskedEmail,
      updated_at: Date.now(),
    });
  };

  const clearPendingLogin = () => {
    storage.removeItem(LOGIN_PENDING_STORAGE_KEY);
  };

  useEffect(() => {
    const pending = getPendingLogin();

    if (pathname === '/' || pathname === '/login') {
      if (pending?.step === 'email') {
        history.replace(LOGIN_ROUTE_EMAIL);
        return;
      }
      if (pending?.step === 'verify') {
        history.replace(LOGIN_ROUTE_VERIFY);
        return;
      }
      history.replace(LOGIN_ROUTE_PHONE);
      return;
    }

    if (!pending) {
      if (!isPhonePath(pathname)) {
        history.replace(LOGIN_ROUTE_PHONE);
      }
      return;
    }

    setPhoneNumber(String(pending.phone));
    setCountryCode(String(pending.calling_code));
    setCodeSentVia(pending.sent_via === 'email' ? 'email' : 'sms');
    setMaskedEmail(pending.masked_email || '');

    if (isEmailPath(pathname) && pending.step !== 'email') {
      history.replace(LOGIN_ROUTE_VERIFY);
    }
  }, [history, pathname]);

  const requestSmsCode = async ({ resend = false } = {}) => {
    const token = await generateSekretKey();
    const client = await makeClient(token);

    return client
      .mutate({
        mutation: REQUEST_CODE,
        variables: {
          input: {
            phone: phoneNumber,
            calling_code: countryCode,
          },
        },
      })
      .then((res) => {
        if (res.data.requestCodeV2) {
          if (resend) toast.success('Code resent successfully.');
          return true;
        }
        showErrorToaster(
          resend ? 'Unable to resend code' : 'Unable to send code',
          null,
          'Please contact info@pitpay.com to request access to the platform.'
        );
        return false;
      })
      .catch((e) => {
        showErrorToaster(
          resend ? 'Unable to resend code' : 'Unable to send code',
          e,
          'Please contact info@pitpay.com to request access to the platform.'
        );
        return false;
      });
  };

  const handleRequestEmailCode = async (
    successMessage = 'Code sent to your email.'
  ) => {
    setEmailLoading(true);
    try {
      const token = await generateSekretKey();
      const client = await makeClient(token);
      const res = await client.mutate({
        mutation: REQUEST_EMAIL_CODE,
        variables: {
          input: {
            phone: phoneNumber,
            calling_code: countryCode,
          },
        },
      });
      const result = res.data.requestEmailCodeV2;
      if (result.success) {
        setCodeSentVia('email');
        setMaskedEmail(result.masked_email);
        setVerificationCode(['', '', '', '', '', '']);
        setVerificationTried(false);
        setError('');
        const emailTarget = result.masked_email || 'your email';
        const isResend = successMessage.toLowerCase().includes('resent');
        setEmailNotice(
          isResend
            ? `Email code resent to ${emailTarget}.`
            : `Email code sent to ${emailTarget}.`
        );
        savePendingLogin({
          step: 'email',
          sentVia: 'email',
          nextMaskedEmail: result.masked_email || '',
        });
        if (!isEmailPath(pathname)) {
          history.push(LOGIN_ROUTE_EMAIL);
        }
        toast.success(successMessage);
      } else {
        const isResend = successMessage.toLowerCase().includes('resent');
        showErrorToaster(
          isResend
            ? 'Unable to resend email code'
            : 'Unable to send email code',
          null,
          'Email verification is not available for this account.'
        );
      }
    } catch (e) {
      const isResend = successMessage.toLowerCase().includes('resent');
      showErrorToaster(
        isResend ? 'Unable to resend email code' : 'Unable to send email code',
        e,
        'Unable to send email code. Please try SMS.'
      );
    }
    setEmailLoading(false);
  };

  const handleResendCode = async () => {
    setVerificationCode(['', '', '', '', '', '']);
    if (isEmailPath(pathname) || codeSentVia === 'email') {
      await handleRequestEmailCode('Code resent to your email.');
    } else {
      const success = await requestSmsCode({ resend: true });
      if (success) {
        savePendingLogin({
          step: 'verify',
          sentVia: 'sms',
          nextMaskedEmail: '',
        });
      }
    }
  };

  const handleEditPhone = () => {
    clearPendingLogin();
    setCodeSentVia('sms');
    setMaskedEmail('');
    setEmailNotice('');
    setVerificationCode(['', '', '', '', '', '']);
    setVerificationTried(false);
    setError('');
    history.push(LOGIN_ROUTE_PHONE);
  };

  const handleRequestCode = async () => {
    const success = await requestSmsCode();
    if (success) {
      setCodeSentVia('sms');
      setMaskedEmail('');
      setEmailNotice('');
      setVerificationCode(['', '', '', '', '', '']);
      setVerificationTried(false);
      setError('');
      savePendingLogin({ step: 'verify', sentVia: 'sms', nextMaskedEmail: '' });
      history.push(LOGIN_ROUTE_VERIFY);
    }
  };

  const handleVerifyCode = async (values) => {
    const code = values.join('');
    if (!verificationTried) {
      const secretKey = await generateSekretKey({
        phone: phoneNumber,
        calling_code: countryCode,
      });

      const res = await props.verifyCodeV2(code, secretKey);

      if (!res.errors) {
        if (res?.data?.verifyCodeV2) {
          if (res.data.verifyCodeV2.role !== 'user') {
            clearPendingLogin();
            storage.set('user', res.data.verifyCodeV2);
            history.push(defaultRedirect);
          } else {
            setVerificationTried(true);
            setError(
              'You do not have access to the dashboard, please contact your promoter to give you staff access.'
            );
          }
        }
      } else {
        setVerificationTried(true);
        setError(res.errors.code);
      }
    }
  };

  const onPhoneStep = isPhonePath(pathname);
  const onEmailStep = isEmailPath(pathname);
  const emailStepNotice =
    emailNotice ||
    (maskedEmail
      ? `Email verification mode is active. Code sent to ${maskedEmail}.`
      : 'Email verification mode is active. Check your inbox for your code.');

  return (
    <>
      <PageContainer>
        <ContentCard>
          {!onPhoneStep ? (
            <>
              <Logo src={getLogo()} alt="Logo" />
              <Title>Enter One Time Password</Title>
              {onEmailStep ? (
                <MaskedEmailText>
                  {maskedEmail
                    ? `A code was sent to ${maskedEmail}`
                    : 'A code was sent to your email'}
                </MaskedEmailText>
              ) : (
                <Subtitle style={{ marginBottom: 8 }}>
                  An SMS code was sent to
                </Subtitle>
              )}

              <PhoneDisplay>
                <PhoneNumber>
                  {formatPhoneForDisplay(phoneNumber, countryCode)}
                </PhoneNumber>
                <EditButton type="button" onClick={handleEditPhone}>
                  Edit
                </EditButton>
              </PhoneDisplay>

              {onEmailStep ? (
                <EmailSuccessNotice role="status" aria-live="polite">
                  {emailStepNotice}
                </EmailSuccessNotice>
              ) : null}

              <OtpInput
                variant="large"
                values={verificationCode}
                onChange={(values) => {
                  setVerificationCode(values);
                  setVerificationTried(false);
                }}
                onComplete={handleVerifyCode}
              />

              {error.length ? <ErrorText>{error}</ErrorText> : null}

              <ResendLink type="button" onClick={handleResendCode}>
                Resend Code
              </ResendLink>

              <HelpHint>
                <HelpHintIcon aria-hidden>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M8 10h.01M12 10h.01M16 10h.01M9 16h6m-9 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </HelpHintIcon>
                <HelpHintContent>
                  <HelpHintTitle>Not receiving your code?</HelpHintTitle>
                  <HelpHintBody>
                    {onEmailStep
                      ? 'Check your email spam or junk folder.'
                      : 'Make sure text message spam filters are turned off on your device.'}
                  </HelpHintBody>
                </HelpHintContent>
              </HelpHint>

              {!onEmailStep && (
                <EmailFallbackLink
                  type="button"
                  style={
                    emailLoading ? { opacity: 0.5, cursor: 'default' } : {}
                  }
                  onClick={
                    emailLoading
                      ? undefined
                      : () => handleRequestEmailCode('Code sent to your email.')
                  }
                >
                  {emailLoading ? 'Sending...' : 'Send code via email instead'}
                </EmailFallbackLink>
              )}

              <Spacer size={16} />
              <HelpLink />
            </>
          ) : (
            <>
              <Logo src={getLogo()} alt="Logo" />
              <Title>Enter Your Mobile Number</Title>
              <Subtitle>
                You will receive a one time password via text message
              </Subtitle>

              <AuthPhoneInput
                variant="glass"
                phoneNumber={phoneNumber}
                countryCode={countryCode}
                onPhoneNumberChange={setPhoneNumber}
                onCountryCodeChange={setCountryCode}
              />

              <ContinueButton
                disabled={!phoneNumber}
                onClick={handleRequestCode}
              >
                Continue &rarr;
              </ContinueButton>

              <Spacer size={16} />
              <HelpLink />
            </>
          )}
        </ContentCard>
      </PageContainer>

      <Toaster
        isOpen={toaster.isOpen}
        onClose={() => setToaster({ ...toaster, isOpen: false })}
        title={toaster.title}
        message={toaster.message}
        type={toaster.type}
        duration={5000}
      />
    </>
  );
};

export default verifyCodeV2(Login(Auth));
